import { founderGlance } from "./founderExperience";
import { deliveredWorkFromReview } from "./deliveredWorkDisplay";
import { isEvaluationTask, taskRequestFingerprint } from "./taskShortTitle";
import { deriveTaskDisplayCopy } from "./taskDisplayCopy";
import { projectTaskWorkspace } from "./taskWorkspaceProjection";

function unpackReview(entry) {
  if (!entry) return { review: null, snapshot: null };
  if (entry.review || entry.snapshot) {
    return { review: entry.review || null, snapshot: entry.snapshot || null };
  }
  return { review: entry, snapshot: null };
}

export function taskOutcome(plan) {
  return deriveTaskDisplayCopy(plan).outcome || founderGlance(plan?.featureRequestText || "").outcome;
}

export function relativeUpdated(value, now = Date.now()) {
  if (!value) return "Updated just now";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "Updated just now";
  const minutes = Math.round(Math.max(0, now - then) / 60000);
  if (minutes < 1) return "Updated just now";
  if (minutes === 1) return "Updated 1 minute ago";
  if (minutes < 60) return `Updated ${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours === 1) return "Updated 1 hour ago";
  if (hours < 24) return `Updated ${hours} hours ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return "Updated 1 day ago";
  return `Updated ${days} days ago`;
}

export function applyDistinctTitles(rows = []) {
  const groups = new Map();
  rows.forEach((row) => {
    const key = row.fingerprint || row.title;
    groups.set(key, [...(groups.get(key) || []), row]);
  });
  return rows.map((row) => {
    const group = groups.get(row.fingerprint || row.title) || [row];
    if (group.length < 2) return { ...row, similarCount: 1 };
    const newest = [...group].sort(
      (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
    )[0];
    if (row.id === newest.id) return { ...row, similarCount: group.length };
    const date = row.updatedAt ? new Date(row.updatedAt) : null;
    const stamp =
      date && !Number.isNaN(date.getTime())
        ? date.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZone: "UTC",
          })
        : "earlier";
    return {
      ...row,
      title: `${row.title} — ${stamp}`,
      similarCount: group.length,
    };
  });
}

function rowCopy(projection, work) {
  if (projection.stage === "Completed") {
    return {
      group: "completed",
      statusLabel: "Complete.",
      ownerLabel: "No one waiting",
      action: { id: "view_completed", label: "View completed Task", section: "review" },
    };
  }
  if (projection.stage === "Planning") {
    const preparing = projection.owner?.role === "none";
    return {
      group: "needsYou",
      statusLabel: preparing
        ? "AI is organizing your request."
        : "One product question needs your answer.",
      ownerLabel: preparing ? "AI is preparing the review" : "Waiting on you",
      action: { id: "continue_plan", label: "Continue planning", section: "task" },
    };
  }
  if (projection.stage === "Ready for handoff") {
    return {
      group: "needsYou",
      statusLabel: "Ready to send to engineering.",
      ownerLabel: "Waiting on you",
      action: { id: "send_to_engineering", label: "Send to engineering", section: "engineering" },
    };
  }
  if (projection.stage === "Waiting for engineering") {
    return {
      group: "withEngineering",
      statusLabel: "Waiting for engineering.",
      ownerLabel: "Waiting on engineering",
      action: { id: "view_handoff", label: "View engineering handoff", section: "engineering" },
    };
  }
  if (projection.stage === "In progress") {
    return {
      group: "withEngineering",
      statusLabel: "Engineering is working on it.",
      ownerLabel: "Waiting on engineering",
      action: { id: "view_handoff", label: "View engineering handoff", section: "engineering" },
    };
  }
  if (projection.status === "Stale" && !work.known) {
    return {
      group: "needsYou",
      statusLabel: "Waiting for the delivered branch.",
      ownerLabel: "Waiting on you",
      action: { id: "connect_delivered_work", label: "Connect delivered work", section: "delivery" },
    };
  }
  if (projection.status === "Stale") {
    return {
      group: "readyToReview",
      statusLabel: "The code changed after the last review.",
      ownerLabel: "Waiting on you",
      action: { id: "review_latest_version", label: "Review latest version", section: "review" },
    };
  }
  if (projection.stage === "Ready for review" && !work.known) {
    return {
      group: "needsYou",
      statusLabel: "Waiting for the delivered branch.",
      ownerLabel: "Waiting on you",
      action: { id: "connect_delivered_work", label: "Connect delivered work", section: "delivery" },
    };
  }
  if (projection.stage === "Ready for review") {
    return {
      group: "readyToReview",
      statusLabel: "Ready for you to review.",
      ownerLabel: "Waiting on you",
      action: { id: "review_this_work", label: "Review this work", section: "review" },
    };
  }
  if (projection.stage === "Waiting for decision" && projection.status === "Blocked") {
    return {
      group: "readyToReview",
      statusLabel: "Waiting for another reviewer.",
      ownerLabel: "Waiting on a reviewer",
      action: { id: "view_review", label: "View review", section: "review" },
    };
  }
  if (projection.stage === "Waiting for decision") {
    return {
      group: "needsYou",
      statusLabel: "Ready for you to review.",
      ownerLabel: "Waiting on you",
      action: { id: "make_decision", label: "Make decision", section: "review" },
    };
  }
  return {
    group: "needsYou",
    statusLabel: projection.statusLabel || "This Task needs attention.",
    ownerLabel: "Waiting on you",
    action: projection.primaryAction || { id: "open", label: "Open Task", section: "task" },
  };
}

export function projectFounderTaskRow(plan, reviewEntry, repo = {}) {
  const { review, snapshot } = unpackReview(reviewEntry);
  const projection = projectTaskWorkspace({
    planStatus: plan.status,
    planError: plan.error,
    latestReview: review || null,
    orchestrationSnapshot: snapshot,
    hasEngAck: String(plan.status || "").toLowerCase() === "completed",
    hasDraft: Boolean(plan.acceptanceCriteria?.length || plan.planIntelligence),
  });
  const work = deliveredWorkFromReview(review, repo, plan);
  const copy = rowCopy(projection, work);
  const display = deriveTaskDisplayCopy(plan);
  return {
    id: plan._id,
    plan,
    title: display.title,
    outcome: display.outcome,
    fingerprint: taskRequestFingerprint(plan.featureRequestText),
    evaluation: isEvaluationTask(plan),
    group: copy.group,
    statusLabel: copy.statusLabel,
    ownerLabel: copy.ownerLabel,
    action: copy.action,
    waitingOnYou: copy.ownerLabel === "Waiting on you",
    updatedAt: plan.updatedAt,
    updatedLabel: relativeUpdated(plan.updatedAt),
    projection,
  };
}

export function projectTasksHome({
  repo = {},
  plans = [],
  reviewsByPlanId = {},
  loading = false,
  reviewsReady = true,
} = {}) {
  const visible = (plans || []).filter(
    (plan) => !isEvaluationTask(plan) && !plan.founderHidden && !plan.archivedAt
  );
  const titled = applyDistinctTitles(
    visible.map((plan) => projectFounderTaskRow(plan, reviewsByPlanId[plan._id], repo))
  );
  const byFingerprint = new Map();
  titled.forEach((row) => {
    const key = `${row.fingerprint || row.id}:${row.group}`;
    byFingerprint.set(key, [...(byFingerprint.get(key) || []), row]);
  });
  const rows = [];
  const seen = new Set();
  titled.forEach((row) => {
    const key = `${row.fingerprint || row.id}:${row.group}`;
    if (seen.has(key)) return;
    seen.add(key);
    const group = byFingerprint.get(key) || [row];
    const newest = [...group].sort(
      (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
    )[0];
    rows.push({ ...newest, similarCount: group.length });
  });
  const groupOf = (id) => rows.filter((row) => row.group === id);
  return {
    repoName: repo.owner && repo.name ? `${repo.owner}/${repo.name}` : "Repository",
    loading,
    reviewsReady,
    rows,
    groups: [
      {
        id: "needsYou",
        title: "Needs your input",
        hint: "Answer, approve, connect work, or decide.",
        items: groupOf("needsYou"),
        collapsedByDefault: false,
      },
      {
        id: "withEngineering",
        title: "With engineering",
        hint: "Engineering has accepted this work or is implementing it.",
        items: groupOf("withEngineering"),
        collapsedByDefault: false,
      },
      {
        id: "readyToReview",
        title: "Ready to review",
        hint: "Delivered work is connected, or a review is waiting.",
        items: groupOf("readyToReview"),
        collapsedByDefault: false,
      },
      {
        id: "completed",
        title: "Completed",
        hint: "Finished Tasks.",
        items: groupOf("completed"),
        collapsedByDefault: true,
      },
    ],
  };
}
